
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

export const useSaveRefundRequest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const saveRequestToDatabase = async (requestData: any): Promise<string | null> => {
    if (!user) return null;
    
    try {
      setIsSaving(true);
      
      const { data, error } = await supabase
        .from("refund_requests")
        .insert({
          user_id: user.id,
          merchant: requestData.merchant,
          product: requestData.product || requestData.merchant, // Use merchant as fallback
          amount: requestData.amount ? parseFloat(requestData.amount) : 0,
          issue: requestData.issue,
          order_number: requestData.order_number,
          status: "pending",
          message_format: requestData.messageFormat || requestData.message_format || "email", // Save the message format
        })
        .select();

      if (error) throw error;
      
      const requestId = data[0].id;
      
      await supabase.from("messages").insert({
        refund_request_id: requestId,
        sender_type: "user",
        content: requestData.issue,
      });

      if (requestData.aiResponse) {
        await supabase.from("messages").insert({
          refund_request_id: requestId,
          sender_type: "ai",
          content: requestData.aiResponse,
          tone: requestData.tone || "polite",
        });
      }

      toast({
        title: "Refund request saved!",
        description: "Your request has been saved to your account",
      });

      navigate(`/conversation/${requestId}`);
      return requestId;
    } catch (error: any) {
      console.error("Error saving refund request:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save refund request",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveRequestToDatabase,
    isSaving
  };
};
